/* eslint-disable react-hooks/exhaustive-deps, array-bracket-newline */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IBinaryResponse, IDexTrade, enums } from '@kattana-io/common';
import { TradesBlock } from '../../../core/models/extended-trades.model';
import { ICurrentState } from '../../../reducers/current';
import { getLiveTradesEventName } from '../../../helpers/events-builder';
import { KattanaAPI, subscription } from '../../../store/createStore';
import eventEmitter from '../../../core/eventEmitter';
import { decodeProtoTradesBlock } from '../../../core/proto-load';
import {
  TradesRef,
  TradesRequestData,
  TradesRequestType,
  TradesResponseData,
  TradesType,
} from '../../../workers/types';
import { TableTrade } from '../../../core/models/table-trade.model';
import { tradesWorker as tradesWorkerCore } from '../../../workers/helper';

const MAX_TRADES_COUNT = 100;
const TRADES_CHECK = 0;
const PAGINATION_PERCENTAGE = 0.9;
const LIMITED_PERCENTAGE = 0.5;
const DEFAULT_USER_MAX_PAGE = 5;

const useGetTrades = (
  ticker: ICurrentState,
  plan: enums.PricingPlan,
  authToken?: string
) => {
  const [trades, setTrades] = useState<TableTrade[]>([]);
  const [loadingNextPage, setLoadingNextPage] = useState(false);

  const tradesRef = useRef<TradesRef>({
    trades: [],
    hashedData: { byWallet: {}, byBlock: {} },
  });
  const liveTradesRef = useRef<IDexTrade[]>([]);
  const limitedRef = useRef(false);
  const loadingRef = useRef(true);
  const pageRef = useRef(1);
  const nextPageCanBeLoadedRef = useRef(false);

  const tradesWorker: Worker = useMemo(() => tradesWorkerCore(), []);

  // enables further pagination when plan changed to premium
  useEffect(() => {
    if (
      plan === enums.PricingPlan.premium &&
      pageRef.current >= DEFAULT_USER_MAX_PAGE
    ) {
      nextPageCanBeLoadedRef.current = true;
    }
  }, [plan]);

  // initial trades request
  useEffect(() => {
    if (window.Worker && ticker.kind === enums.ETickerKind.DEX) {
      let flag = true;
      KattanaAPI.binary
        .trades(ticker.chain, ticker.address)
        .then((resp: IBinaryResponse) => {
          if (flag) {
            const decodedData: TradesBlock = decodeProtoTradesBlock(resp.data);
            if (Array.isArray(decodedData?.Trades)) {
              nextPageCanBeLoadedRef.current =
                decodedData.Trades.length > TRADES_CHECK;

              const request: TradesRequestData = {
                ticker,
                trades: decodedData.Trades,
                tradesType: TradesType.trade,
                type: TradesRequestType.setTrades,
              };

              tradesWorker.postMessage(JSON.stringify(request));
            } else {
              loadingRef.current = false;
              nextPageCanBeLoadedRef.current = false;

              setTrades([]);
            }
          }
        })
        .catch((e) => {
          console.warn(e);
        });

      return () => {
        flag = false;

        tradesRef.current.trades = [];
        tradesRef.current.hashedData = { byWallet: {}, byBlock: {} };
        liveTradesRef.current = [];
        loadingRef.current = true;
        pageRef.current = 1;
        nextPageCanBeLoadedRef.current = false;

        setTrades([]);
        setLoadingNextPage(false);
      };
    }
  }, [ticker.id]);

  // loads more trades when table scrolled to the end
  useEffect(() => {
    if (loadingNextPage && window.Worker) {
      let flag = true;
      const currentTrades = tradesRef.current.trades;
      KattanaAPI.binary
        .trades(
          ticker.chain,
          ticker.address,
          authToken,
          pageRef.current + 1,
          currentTrades[currentTrades.length - 1].timestamp
        )
        .then((resp: IBinaryResponse) => {
          if (flag) {
            const decodedData: TradesBlock = decodeProtoTradesBlock(resp.data);
            if (Array.isArray(decodedData?.Trades)) {
              nextPageCanBeLoadedRef.current =
                decodedData.Trades.length > TRADES_CHECK;

              const request: TradesRequestData = {
                ticker,
                trades: tradesRef.current.trades,
                hashedData: tradesRef.current.hashedData,
                tradesToAppend: decodedData.Trades,
                type: TradesRequestType.appendEndTrades,
              };

              tradesWorker.postMessage(JSON.stringify(request));
              pageRef.current += 1;
            } else {
              nextPageCanBeLoadedRef.current = false;
            }
          }
        })
        .catch((e) => {
          console.warn(e);
        })
        .finally(() => {
          if (flag) {
            setLoadingNextPage(false);
          }
        });

      return () => {
        flag = false;
      };
    }
  }, [loadingNextPage, ticker.id]);

  // handles worker messages
  useEffect(() => {
    if (window.Worker) {
      try {
        tradesWorker.onmessage = (e: MessageEvent<string>) => {
          const { loading, trades, hashedData } = JSON.parse(
            e.data
          ) as unknown as TradesResponseData;

          tradesRef.current.trades = trades;
          tradesRef.current.hashedData = hashedData;
          loadingRef.current = loading;

          setTrades(tradesRef.current.trades);
        };
      } catch (error) {
        console.error('Failed to receive message from trades web-worker');
      }
    }
  }, [tradesWorker]);

  // appends live trades to table every 1 second
  useEffect(() => {
    const timer = window.setInterval(() => {
      const canBeAdded =
        liveTradesRef.current.length &&
        window.Worker &&
        !limitedRef.current &&
        !loadingRef.current;

      if (canBeAdded) {
        const request: TradesRequestData = {
          ticker,
          trades: tradesRef.current.trades,
          hashedData: tradesRef.current.hashedData,
          tradesToAppend: liveTradesRef.current,
          type:
            tradesRef.current.trades.length < MAX_TRADES_COUNT
              ? TradesRequestType.appendStartTrades
              : TradesRequestType.appendStartTradesWithReplace,
        };

        tradesWorker.postMessage(JSON.stringify(request));
        liveTradesRef.current = [];
      }
    }, 1000);

    return () => window.clearInterval(timer);
  }, [ticker.id]);

  // subscribes to live trades
  useEffect(() => {
    if (ticker.kind === enums.ETickerKind.DEX) {
      const lastTradeHandler = ({ trade }: { trade: IDexTrade }) => {
        if (trade) {
          liveTradesRef.current.unshift(trade);
        }
      };

      const eventName = getLiveTradesEventName(ticker.chain, ticker.address);
      subscription.subscribeToPrimaryTrades(ticker.chain, ticker.address);
      eventEmitter.on(eventName, lastTradeHandler);

      return () => {
        eventEmitter.removeListener(eventName, lastTradeHandler);
        subscription.unsubscribeFromPrimaryTrades();
      };
    }
  }, [ticker.id]);

  // terminates worker on unmount
  useEffect(() => () => tradesWorker.terminate(), []);

  /**
   * handles load next page and enables live trades
   */
  const onScroll = useCallback(
    (listHeight: number, scrollPos: number, isInitialPos: boolean) => {
      const paginationCheck = scrollPos > listHeight * PAGINATION_PERCENTAGE;
      if (
        paginationCheck &&
        nextPageCanBeLoadedRef.current &&
        !loadingNextPage
      ) {
        setLoadingNextPage(true);
      }
      const limitedCheck =
        scrollPos > listHeight * LIMITED_PERCENTAGE &&
        tradesRef.current.trades.length > MAX_TRADES_COUNT / 1.5;
      if (limitedCheck) {
        limitedRef.current = true;
      } else {
        limitedRef.current = false;
      }
      if (isInitialPos && tradesRef.current.trades.length > MAX_TRADES_COUNT) {
        const request: TradesRequestData = {
          ticker,
          trades: tradesRef.current.trades.slice(0, MAX_TRADES_COUNT),
          tradesType: TradesType.tableTrade,
          type: TradesRequestType.setTrades,
        };

        tradesWorker.postMessage(JSON.stringify(request));
        nextPageCanBeLoadedRef.current = true;
        pageRef.current = 1;
      }
    },
    [loadingNextPage, ticker.id]
  );

  const data = useMemo(
    () => ({
      loading: loadingRef.current,
      trades,
      nextPageCanBeLoaded: nextPageCanBeLoadedRef.current,
      onScroll,
    }),
    [trades, onScroll]
  );

  return data;
};

export { useGetTrades };
